import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';
import { usePermissions } from '@/hooks/usePermissions';
import {
  getOrganizationByCommerce,
  listOrganizationContacts,
  listOrganizationCustomers,
  listOrganizationDepartments,
  listOrganizationEmployees,
  listOrganizationLocations,
  listOrganizationMemberships,
  listOrganizationProfessionals,
  listRolesForOrganization,
  fetchUserLabels,
  fetchCustomerLinkStats,
  fetchContactLinkStats,
  fetchEmployeePerformance,
  syncMerchantCustomersToCore,
  syncMerchantPeopleToCore,
  type CoreContact,
  type CoreCustomer,
  type CoreDepartment,
  type CoreEmployee,
  type CoreLocation,
  type CoreMembership,
  type CoreOrganization,
  type CoreProfessional,
  type CoreRole,
  type EmployeePerformance,
  type UserLabel,
} from '@/lib/zifekCore';
import LocationsManager from './components/LocationsManager';
import MembersManager from './components/MembersManager';
import RolesManager from './components/RolesManager';
import PeopleManager from './components/PeopleManager';
import DepartmentsManager from './components/DepartmentsManager';
import EmployeesManager from './components/EmployeesManager';

type Tab = 'locations' | 'members' | 'departments' | 'employees' | 'roles' | 'people';

// Chaque onglet interne est soumis à une permission : un rôle sans la
// permission requise ne voit pas l'onglet (logique alignée sur les menus).
const TABS: { key: Tab; label: string; icon: string; permission: string }[] = [
  { key: 'locations', label: 'Établissements', icon: 'ri-store-2-line', permission: 'locations.read' },
  { key: 'members', label: 'Membres', icon: 'ri-group-line', permission: 'members.read' },
  { key: 'departments', label: 'Départements', icon: 'ri-node-tree', permission: 'members.read' },
  { key: 'employees', label: 'Employés', icon: 'ri-team-line', permission: 'members.read' },
  { key: 'roles', label: 'Rôles & permissions', icon: 'ri-shield-user-line', permission: 'roles.manage' },
  { key: 'people', label: 'Personnes', icon: 'ri-contacts-book-2-line', permission: 'customers.read' },
];

export default function DashboardCorePage() {
  const { user } = useAuth();
  const { has } = usePermissions();
  const [tab, setTab] = useState<Tab>('locations');
  const [org, setOrg] = useState<CoreOrganization | null>(null);
  const [locations, setLocations] = useState<CoreLocation[]>([]);
  const [memberships, setMemberships] = useState<CoreMembership[]>([]);
  const [roles, setRoles] = useState<CoreRole[]>([]);
  const [contacts, setContacts] = useState<CoreContact[]>([]);
  const [customers, setCustomers] = useState<CoreCustomer[]>([]);
  const [professionals, setProfessionals] = useState<CoreProfessional[]>([]);
  const [departments, setDepartments] = useState<CoreDepartment[]>([]);
  const [employees, setEmployees] = useState<CoreEmployee[]>([]);
  const [users, setUsers] = useState<Record<number, UserLabel>>({});
  const [performance, setPerformance] = useState<Record<number, EmployeePerformance>>({});
  const [linkStats, setLinkStats] = useState<{ creditLinked: number[]; orderCounts: Record<number, number> }>({ creditLinked: [], orderCounts: {} });
  const [contactLinks, setContactLinks] = useState<{ supplierLinked: number[]; partnerLinked: number[] }>({ supplierLinked: [], partnerLinked: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const syncedRef = useRef(false);

  const commerceId = useMemo(() => getCommerceId(user), [user]);

  // Onglets visibles selon le rôle du membre connecté.
  const allowedTabs = TABS.filter((t) => has(t.permission));
  const allowedKeys = allowedTabs.map((t) => t.key).join(',');

  const load = useCallback(async () => {
    if (!commerceId) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const organization = await getOrganizationByCommerce(commerceId);
      setOrg(organization);
      if (!organization) { setLoading(false); return; }

      // Fiche unique : rapproche le carnet de crédit, les commandes, les
      // fournisseurs et les partenaires vers le socle Personnes (une fois
      // par visite pour rester léger).
      if (!syncedRef.current) {
        syncedRef.current = true;
        await Promise.all([
          syncMerchantCustomersToCore(commerceId),
          syncMerchantPeopleToCore(commerceId),
        ]);
      }

      const [loc, memb, rl, ct, cu, pr, dp, em] = await Promise.all([
        listOrganizationLocations(organization.id),
        listOrganizationMemberships(organization.id),
        listRolesForOrganization(organization.id),
        listOrganizationContacts(organization.id),
        listOrganizationCustomers(organization.id),
        listOrganizationProfessionals(organization.id),
        listOrganizationDepartments(organization.id),
        listOrganizationEmployees(organization.id),
      ]);
      setLocations(loc);
      setMemberships(memb);
      setRoles(rl);
      setContacts(ct);
      setCustomers(cu);
      setProfessionals(pr);
      setDepartments(dp);
      setEmployees(em);

      const labels = await fetchUserLabels([...memb.map((m) => m.user_id), organization.owner_user_id || 0]);
      setUsers(labels);
      const [customerStats, contactsStats, perf] = await Promise.all([
        fetchCustomerLinkStats(commerceId),
        fetchContactLinkStats(commerceId),
        fetchEmployeePerformance(commerceId),
      ]);
      setLinkStats(customerStats);
      setContactLinks(contactsStats);
      setPerformance(perf);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger votre espace Core.');
    } finally {
      setLoading(false);
    }
  }, [commerceId]);

  useEffect(() => { load(); }, [load]);

  // Si l'onglet courant n'est plus autorisé, on bascule sur le premier permis.
  useEffect(() => {
    if (!allowedTabs.some((t) => t.key === tab) && allowedTabs[0]) {
      setTab(allowedTabs[0].key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedKeys]);

  const stats = [
    { label: 'Établissements', value: locations.length, icon: 'ri-store-2-line', tone: 'secondary' },
    { label: 'Membres', value: memberships.length, icon: 'ri-group-line', tone: 'primary' },
    { label: 'Départements', value: departments.length, icon: 'ri-node-tree', tone: 'accent' },
    { label: 'Employés', value: employees.length, icon: 'ri-team-line', tone: 'foreground' },
  ] as const;

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-organization-chart text-primary-600"></i>
            Mon entreprise
          </h2>
          <p className="text-sm text-foreground-500 mt-1">
            Gérez vos établissements, votre équipe, les départements, les rôles et les personnes de votre organisation.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70 disabled:opacity-50 transition-colors"
        >
          <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
          Actualiser
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
        </div>
      ) : error ? (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center justify-between gap-3">
          <span className="flex items-center gap-2"><i className="ri-error-warning-line"></i>{error}</span>
          <button onClick={load} className="text-xs underline cursor-pointer whitespace-nowrap">Réessayer</button>
        </div>
      ) : !org ? (
        <div className="flex flex-col items-center justify-center py-20 bg-background-50 border border-background-200/70 rounded-lg text-center px-6">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-building-2-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-700 font-medium">Votre organisation n’est pas encore initialisée.</p>
          <p className="text-sm text-foreground-500 mt-1 max-w-md">
            Le socle de votre entreprise est normalement créé automatiquement. Contactez le support si le problème persiste.
          </p>
          <button onClick={load} className="mt-4 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            Réessayer
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
            {stats.map((s) => (
              <div key={s.label} className="bg-background-50 border border-background-200/70 rounded-lg p-4">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${
                  s.tone === 'primary' ? 'bg-primary-100 text-primary-700'
                    : s.tone === 'secondary' ? 'bg-secondary-100 text-secondary-700'
                    : s.tone === 'accent' ? 'bg-accent-100 text-accent-900'
                    : 'bg-foreground-100 text-foreground-700'
                }`}>
                  <i className={`${s.icon} text-lg`}></i>
                </div>
                <div className="text-xl font-bold font-heading text-foreground-950">{s.value}</div>
                <div className="text-xs text-foreground-500">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 mb-5 w-full sm:w-fit overflow-x-auto">
            {allowedTabs.map((tb) => (
              <button
                key={tb.key}
                onClick={() => setTab(tb.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  tab === tb.key ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <i className={tb.icon}></i>
                {tb.label}
              </button>
            ))}
          </div>

          {tab === 'locations' && <LocationsManager org={org} locations={locations} onChanged={load} />}
          {tab === 'members' && <MembersManager org={org} memberships={memberships} roles={roles} users={users} onChanged={load} />}
          {tab === 'departments' && <DepartmentsManager org={org} departments={departments} users={users} onChanged={load} />}
          {tab === 'employees' && (
            <EmployeesManager
              org={org}
              employees={employees}
              departments={departments}
              locations={locations}
              users={users}
              performance={performance}
              onChanged={load}
            />
          )}
          {tab === 'roles' && <RolesManager org={org} roles={roles} onChanged={load} />}
          {tab === 'people' && (
            <PeopleManager
              org={org}
              contacts={contacts}
              customers={customers}
              professionals={professionals}
              creditLinked={linkStats.creditLinked}
              orderCounts={linkStats.orderCounts}
              supplierLinked={contactLinks.supplierLinked}
              partnerLinked={contactLinks.partnerLinked}
              onChanged={load}
            />
          )}
        </>
      )}
    </div>
  );
}